'use client';

import { useState, useEffect } from 'react';
import { Copy, Check, AlertCircle, Upload, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { graphqlFormatterContent } from '../../data/graphql-formatter-content';

// Simple GraphQL formatter function
const formatGraphQL = (input: string, indentSize: number = 2): string => {
    if (!input.trim()) return '';

    let output = '';
    let indent = 0;
    let inString = false;
    let currentStringChar = '';
    let skipNext = false;

    for (let i = 0; i < input.length; i++) {
        const char = input[i];

        if (skipNext) {
            skipNext = false;
            output += char;
            continue;
        }

        // Handle strings
        if ((char === '"' || char === "'") && (i === 0 || input[i - 1] !== '\\')) {
            if (!inString) {
                inString = true;
                currentStringChar = char;
            } else if (char === currentStringChar) {
                inString = false;
            }
        }

        if (inString) {
            output += char;
            continue;
        }

        // Handle special characters
        switch (char) {
            case '{':
            case '[':
                output += char;
                indent += indentSize;
                output += '\n' + ' '.repeat(indent);
                break;

            case '}':
            case ']':
                indent = Math.max(0, indent - indentSize);
                output += '\n' + ' '.repeat(indent) + char;
                break;

            case ':':
                output += ': ';
                break;

            case ',':
                output += ',\n' + ' '.repeat(indent);
                // Skip any whitespace after comma
                while ([' ', '\t', '\n', '\r'].includes(input[i + 1])) i++;
                break;

            case ' ':
            case '\t':
            case '\n':
            case '\r':
                // Skip extra whitespace
                if (output[output.length - 1] !== ' ' &&
                    output[output.length - 1] !== '\n') {
                    output += ' ';
                }
                break;

            default:
                output += char;
        }
    }

    // Clean up any remaining whitespace issues
    return output
        .replace(/\n\s*\n/g, '\n')
        .replace(/\{\s*\}/g, '{}')
        .replace(/\[\s*\]/g, '[]')
        .trim();
};

export default function GraphQLFormatter() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [indentSize, setIndentSize] = useState(2);
    const [wrapLines, setWrapLines] = useState(false);

    useEffect(() => {
        // Load any saved input from localStorage
        const savedInput = localStorage.getItem('graphqlFormatterInput');
        if (savedInput) {
            setInput(savedInput);
        }
    }, []);

    const formatGraphQLCode = () => {
        try {
            if (!input.trim()) {
                setError('Please enter some GraphQL to format');
                return;
            }

            const formatted = formatGraphQL(input, indentSize);
            setOutput(formatted);
            setError('');

            // Save to localStorage
            localStorage.setItem('graphqlFormatterInput', input);
            toast.success('GraphQL formatted successfully!');
        } catch (err) {
            setError('Error formatting GraphQL: ' + (err as Error).message);
            setOutput('');
        }
    };

    const copyToClipboard = () => {
        if (!output) return;

        navigator.clipboard.writeText(output).then(() => {
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        setError('');
        localStorage.removeItem('graphqlFormatterInput');
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);
    };

    const downloadFile = () => {
        if (!output) return;
        const blob = new Blob([output], { type: 'application/graphql' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'query.graphql';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const loadSample = () => {
        const sampleQuery = `query GetUserWithPosts($userId: ID!, $limit: Int = 5) {
  user(id: $userId) {
    id
    name
    email
    posts(limit: $limit) {
      id
      title
      body
      comments {
        id
        text
        author {
          id
          name
        }
      }
    }
  }
}`;

        setInput(sampleQuery);
        toast.success('Sample GraphQL query loaded!');
    };

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6">
            <div className="mb-8">
                <ToolPageIntro title="GraphQL Formatter">
                Format and beautify GraphQL queries with our free online tool. Clean up GraphQL code with proper indentation and syntax highlighting.
            </ToolPageIntro>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <label htmlFor="input" className="block text-sm font-medium text-gray-700">
                            Input GraphQL
                        </label>
                        <div className="flex space-x-2">
                            <button
                                onClick={loadSample}
                                className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded hover:bg-blue-100 transition-colors"
                            >
                                Load Sample
                            </button>
                            <label className="cursor-pointer px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors flex items-center">
                                <Upload className="h-4 w-4 mr-1" />
                                Upload
                                <input
                                    type="file"
                                    accept=".graphql,.gql"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                />
                            </label>
                            <button
                                onClick={clearAll}
                                className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                    <div className="relative">
                        <textarea
                            id="input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            className="w-full h-96 font-mono text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={`# Paste your GraphQL query or schema here
query {
  # Example query
  user(id: "1") {
    id
    name
  }
}`}
                            spellCheck="false"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <label htmlFor="output" className="block text-sm font-medium text-gray-700">
                            Formatted GraphQL
                        </label>
                        <div className="flex items-center space-x-2">
                            <div className="flex items-center">
                                <label htmlFor="indent" className="text-xs text-gray-500 mr-2">
                                    Indent:
                                </label>
                                <select
                                    id="indent"
                                    value={indentSize}
                                    onChange={(e) => setIndentSize(Number(e.target.value))}
                                    className="text-sm border border-gray-300 rounded px-2 py-1"
                                >
                                    <option value={2}>2 spaces</option>
                                    <option value={4}>4 spaces</option>
                                </select>
                            </div>
                            <button
                                onClick={copyToClipboard}
                                disabled={!output}
                                className={`p-1.5 rounded ${output
                                    ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                    : 'text-gray-300 cursor-not-allowed'
                                    }`}
                                title="Copy to clipboard"
                            >
                                {copied ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                    <Copy className="h-4 w-4" />
                                )}
                            </button>
                            <button
                                onClick={downloadFile}
                                disabled={!output}
                                className={`p-1.5 rounded ${output
                                    ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                    : 'text-gray-300 cursor-not-allowed'
                                    }`}
                                title="Download file"
                            >
                                <Download className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    <div className="relative">
                        <pre className={`h-96 overflow-auto p-3 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono ${wrapLines ? 'whitespace-pre-wrap' : 'whitespace-pre'
                            }`}>
                            <code className="text-gray-800">
                                {output || (error ? '' : 'Formatted GraphQL will appear here...')}
                            </code>
                        </pre>
                        {error && (
                            <div className="absolute bottom-2 left-2 right-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm flex items-start">
                                <AlertCircle className="h-4 w-4 mr-1.5 mt-0.5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex justify-center mb-8">
                <button
                    onClick={formatGraphQLCode}
                    disabled={!input.trim()}
                    className={`px-6 py-2 rounded-md text-white font-medium ${input.trim()
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-gray-400 cursor-not-allowed'
                        } transition-colors`}
                >
                    Format GraphQL
                </button>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h3 className="font-medium text-blue-800 mb-2">
                    How to use the GraphQL Formatter
                </h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
                    <li>
                        Paste your GraphQL query or schema in the left panel,
                        upload a .graphql file, or click "Load Sample"
                    </li>
                    <li>Click the "Format GraphQL" button to format the code</li>
                    <li>Use the indentation options to adjust the output format</li>
                    <li>Copy or download the formatted GraphQL</li>
                </ol>
            </div>

            {/* SEO CONTENT */}
            <div className="mt-16">
                {/* SEO CONTENT */}
                <InformationalContent
                    title={graphqlFormatterContent.title}
                    description={graphqlFormatterContent.description}
                    content={graphqlFormatterContent.content}
                    faqs={graphqlFormatterContent.faqs}
                    relatedTools={graphqlFormatterContent.relatedTools}
                />
            </div>
        </div>
    );
}
